import axios from 'axios';
import i18n from '../i18n';
import { store, persistor } from '../app/store';
import { setAuthCredentials, logoutSuccess } from '../features/auth/authSlice';
import { AIProposedUpdate } from '../types/supervisor';
import { API_BASE_URL } from '../config/apiBaseUrl';
import { logging } from './logging';

// Define a type for the conversation history messages
interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

type ChatRequestSource = 'user' | 'interface' | 'supervisor';

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
};

const extractErrorStack = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.stack;
  }
  return undefined;
};

const extractResponseMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message.trim().length > 0 ? message.trim() : undefined;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // This is crucial for sending cookies
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

api.interceptors.request.use((config) => {
  const locale = i18n.language;
  if (locale) {
    config.headers = config.headers ?? {};
    config.headers['x-user-locale'] = locale;
  }
  return config;
});

api.interceptors.response.use(
  response => {
    const requestId = response.headers?.['x-request-id'];
    if (typeof requestId === 'string') {
      logging.setRequestId(requestId);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestId = error.response?.headers?.['x-request-id'];
    if (typeof requestId === 'string') {
      logging.setRequestId(requestId);
    }

    // Check if the error is 401, not from a refresh request, and we're not already refreshing
    // Prevent refresh loop if the original request was also a refresh request
    // Also skip refresh for login/register endpoints
    const isAuthRequest = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthRequest) {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(async (resolve, reject) => {
        try {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
          const user = data;
          store.dispatch(setAuthCredentials(user));
          
          processQueue(null);
          resolve(api(originalRequest));
        } catch (refreshError) {
          processQueue(refreshError);
          const authState = store.getState().auth;
          const hadAuthenticatedSession = Boolean(authState.isAuthenticated || authState.user);

          if (hadAuthenticatedSession) {
            store.dispatch(logoutSuccess());
            persistor.purge();
          }
          reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      });
    }

    const responseMessage = extractResponseMessage(error);
    if (responseMessage && error instanceof Error) {
      error.message = responseMessage;
    }

    logging.error('api.request_failed', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      responseMessage,
      errorMessage: extractErrorMessage(error),
      errorStack: extractErrorStack(error),
    });

    return Promise.reject(error);
  }
);

export const getAiProposedUpdate = async (
  conversationHistory: ChatHistoryMessage[],
  orderState: Record<string, unknown>,
  language: string,
  options?: { source?: ChatRequestSource; promptMode?: 'default' | 'external_integrations' }
): Promise<AIProposedUpdate> => {
  const state = store.getState();
  const { user, temporaryUserId } = state.auth;
  const userId = user?.id;

  const response = await api.post('/chat/propose-update', {
    conversationHistory,
    orderState,
    userId,
    temporaryUserId,
    language,
    requestSource: options?.source ?? 'user',
    promptMode: options?.promptMode ?? 'default',
  });

  return response.data;
};

export default api;
